/**
 * ============================================================================
 *  File        : frontend/src/pages/DashboardPage.tsx
 *  Module      : pages
 *  Purpose     : 傻瓜使用者用的營運工作台，呈現下一步與模組入口
 *  Author      : Phoenix Lai
 *  Created     : 2026-05-30
 *  Updated     : 2026-05-30
 *  License     : Proprietary - ECO FAITH Platform v3.0
 * ============================================================================
 */

import { ArrowRight, ClipboardCheck } from 'lucide-react'
import type { ReactElement } from 'react'
import { Link } from 'react-router-dom'
import { modules } from '../data/navigation'
import { usePermissions } from '../hooks/usePermissions'

interface DashboardPageProps {
  t: (key: string) => string
}

const metrics = [
  'dashboard.metricCases',
  'dashboard.metricShipments',
  'dashboard.metricInventory',
  'dashboard.metricAudit',
]

export function DashboardPage({ t }: DashboardPageProps): ReactElement {
  const { can } = usePermissions()
  return (
    <section className="page-stack">
      <header className="page-heading">
        <div>
          <span className="eyebrow">{t('app.name')}</span>
          <h1>{t('dashboard.title')}</h1>
          <p>{t('dashboard.subtitle')}</p>
        </div>
      </header>

      <div className="metric-grid">
        {metrics.map((metric, index) => (
          <article className="metric-card" key={metric}>
            <span>{t(metric)}</span>
            <strong>{index === 0 ? '0' : '-'}</strong>
          </article>
        ))}
      </div>

      <section className="task-band">
        <div>
          <span className="eyebrow">{t('dashboard.nextActions')}</span>
          <h2>{t('desc.dashboard')}</h2>
        </div>
        <ClipboardCheck aria-hidden="true" size={32} />
      </section>

      <section className="module-grid" aria-label={t('dashboard.moduleHealth')}>
        {modules
          .filter((module) => module.id !== 'dashboard')
          .filter((module) => module.disabled || can(module.permissionKey))
          .map((module) => (
            <Link className="module-card" key={module.id} to={module.path}>
              <div>
                <span className={`status-pill ${module.status}`}>{t(`status.${module.status}`)}</span>
                <h2>{t(module.labelKey)}</h2>
                <p>{t(module.descriptionKey)}</p>
              </div>
              <div className="module-card-footer">
                <span>{module.counters.map((key) => t(key)).join(' / ')}</span>
                <ArrowRight aria-hidden="true" size={18} />
              </div>
            </Link>
          ))}
      </section>
    </section>
  )
}
